import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api/client';
import type { Marca, Produto } from '../lib/api/types';
import { useAuth } from '../lib/auth/AuthContext';
import { PageHeader } from '../components/ui/PageHeader';
import { Carregando, Erro, Vazio } from '../components/ui/Estados';

/**
 * Os produtos (A6).
 *
 * <p>O cadastro e a lista. A mídia e a publicação de cada um são a A7, na tela de dentro —
 * clicar no nome abre. A coluna da direita responde à pergunta que se faz olhando a lista:
 * o que já está no ar.
 */
export function ProdutosPage() {
  const { temPapel } = useAuth();
  const clienteDeConsulta = useQueryClient();
  const [brandId, setBrandId] = useState('');
  const [sku, setSku] = useState('');
  const [nome, setNome] = useState('');
  const [gtin, setGtin] = useState('');

  const produtos = useQuery({
    queryKey: ['produtos'],
    queryFn: () => api.get<Produto[]>('/console/products'),
  });
  const marcas = useQuery({
    queryKey: ['marcas'],
    queryFn: () => api.get<Marca[]>('/console/brands'),
  });

  const criar = useMutation({
    mutationFn: () =>
      api.post<Produto>('/console/products', {
        brandId: brandId || marcas.data?.[0]?.id,
        sku,
        name: nome,
        gtin: gtin || null,
      }),
    onSuccess: () => {
      setSku('');
      setNome('');
      setGtin('');
      void clienteDeConsulta.invalidateQueries({ queryKey: ['produtos'] });
    },
  });

  const porMarca = new Map((marcas.data ?? []).map((m) => [m.id, m.name]));

  return (
    <>
      <PageHeader
        titulo="Produtos"
        subtitulo="O que a marca fabrica. Um lote é sempre de um produto, e um item é sempre de um lote."
      />
      <div className="wrap">
        <div className="duas">
          <div className="card tbl">
            {produtos.isPending && <Carregando o="os produtos" />}
            {produtos.isError && <Erro erro={produtos.error} />}
            {produtos.data && produtos.data.length === 0 && (
              <Vazio titulo="Nenhum produto" detalhe="Cadastre o primeiro ao lado." />
            )}
            {produtos.data && produtos.data.length > 0 && (
              <table className="t">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Produto</th>
                    <th>Marca</th>
                    <th>GTIN</th>
                    <th>Página pública</th>
                  </tr>
                </thead>
                <tbody>
                  {produtos.data.map((p) => (
                    <tr key={p.id}>
                      <td className="mono-s">{p.sku}</td>
                      <td style={{ fontWeight: 600 }}>
                        <Link to={`/produtos/${p.id}`}>{p.name}</Link>
                      </td>
                      <td>{porMarca.get(p.brandId) ?? '—'}</td>
                      <td className="mono-s">{p.gtin ?? <span className="dash">—</span>}</td>
                      {/* O estado da publicação fica na lista, e não só na tela de dentro:
                          é a pergunta "o que já está no ar?", e ela se faz olhando a
                          coluna, não abrindo doze produtos. */}
                      <td>
                        <span
                          className={`chip ${
                            p.status === 'PUBLISHED' ? 'chip-teal' : 'chip-neutral'
                          }`}
                        >
                          {p.status === 'PUBLISHED' ? 'Publicado' : 'Rascunho'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {temPapel('COMPANY_ADMIN', 'COMPANY_CATALOG') && (
            <div className="card cd">
              <div className="cd2-t">Novo produto</div>
              <form
                className="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  criar.mutate();
                }}
              >
                <div className="campo">
                  <label htmlFor="marca">Marca</label>
                  <select
                    id="marca"
                    value={brandId || (marcas.data?.[0]?.id ?? '')}
                    onChange={(e) => setBrandId(e.target.value)}
                  >
                    {(marcas.data ?? []).map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="campo">
                  <label htmlFor="sku">SKU</label>
                  <input id="sku" value={sku} onChange={(e) => setSku(e.target.value)} required />
                </div>
                <div className="campo">
                  <label htmlFor="nome">Nome</label>
                  <input
                    id="nome"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    required
                  />
                  <span className="dica">É este nome que o consumidor lê no passaporte.</span>
                </div>
                <div className="campo">
                  <label htmlFor="gtin">GTIN</label>
                  <input id="gtin" value={gtin} onChange={(e) => setGtin(e.target.value)} />
                  <span className="dica">
                    Opcional. Sem GTIN de verdade, a forma longa do Digital Link não é
                    publicada — conformidade GS1 sobre número inventado seria conformidade de
                    mentira (ADR-045).
                  </span>
                </div>
                {criar.isError && <div className="aviso ruim">{String(criar.error)}</div>}
                <button className="btn btn-primary" type="submit" disabled={criar.isPending}>
                  Criar produto
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
